import { FC, useState } from 'react';
import { EntityData, GroupDetails, OUDetails } from '../types';
import { EmptyStateIcons, SeverityLevel, Table, TableColumn, Tabs } from '@silverfort/sf-components';
import { AD_ENTITY_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import { usePricingModulesState } from '../../../context/contexts/PricingModuleContext';
import { InventoryEntity } from '../../Inventory/types';

const createPropertyRow = (title, data) => {
    if (data === undefined || !title) {
        return <></>;
    }

    return (
        <div className="flex w-full items-center">
            <div className="h-4 w-[200px] text-sf-gray-800 font-medium text-13">{title}</div>
            <div className="h-4 text-sf-gray-500 text-13">{data || 'N/A'}</div>
        </div>
    );
};

const renderPropertiesTab = (entityData: EntityData) => {
    const details = entityData.details as GroupDetails;
    return (
        <div className="border h-full w-full border-sf-gray-100 py-2 px-6 rounded">
            <div className="flex h-full flex-col gap-4">
                {createPropertyRow('Domain', details.domain_name)}
                {createPropertyRow('Name', details.name)}
                {createPropertyRow('Guid', details.guid)}
                {createPropertyRow('Path', details.path)}
                {createPropertyRow('SID', details.sid)}
            </div>
        </div>
    );
};

const buildUserColumns = (pricingModulesState) => {
    const columns: TableColumn<InventoryEntity>[] = [
        { columnKey: 'display_name', title: 'Name' },
        { columnKey: 'identifier', title: 'User name' }
    ];

    if (pricingModulesState?.ispm?.anyEnabled || pricingModulesState?.itdr?.anyEnabled) {
        columns.push({
            columnKey: 'risk_level',
            title: 'Risk level',
            render: ({ risk_level }) => (
                <div className="w-9">
                    <SeverityLevel severity={risk_level || 1} />
                </div>
            )
        });
    }

    return columns;
};

const renderTabData = (entityData, selectedTab, onEntityClick, pricingModulesState) => {
    switch (selectedTab) {
        case 0:
            return renderPropertiesTab(entityData);
        case 1:
            return (
                <Table
                    onRowClick={(item: InventoryEntity) =>
                        onEntityClick(item.guid, item.display_name, AD_ENTITY_TYPES.USER)
                    }
                    columns={buildUserColumns(pricingModulesState)}
                    bodyHeight={460}
                    rows={entityData.details.users}
                    EmptyStateProps={{ title: 'No nested users', icon: EmptyStateIcons.NoItems }}
                />
            );
        case 2:
            return (
                <Table
                    columns={[
                        { columnKey: 'name', title: 'Name' },
                        { columnKey: 'sid', title: 'SID' },
                        { columnKey: 'path', title: 'Path' }
                    ]}
                    bodyHeight={460}
                    rows={entityData.details.groups}
                    onRowClick={(item: InventoryEntity) => onEntityClick(item.guid, item.name, AD_ENTITY_TYPES.GROUP)}
                    EmptyStateProps={{ title: 'No nested groups', icon: EmptyStateIcons.NoItems }}
                />
            );

        case 3:
            return (
                <Table
                    columns={[
                        { columnKey: 'display_name', title: 'Name' },
                        { columnKey: 'operating_system', title: 'Operating system' }
                    ]}
                    bodyHeight={460}
                    rows={entityData.details.resources}
                    onRowClick={(item: InventoryEntity) =>
                        onEntityClick(item.guid, item.display_name, AD_ENTITY_TYPES.SERVER)
                    }
                    EmptyStateProps={{ title: 'No nested resources', icon: EmptyStateIcons.NoItems }}
                />
            );
        case 4:
            return (
                <Table
                    columns={[
                        { columnKey: 'name', title: 'Name' },
                        { columnKey: 'sid', title: 'SID' },
                        { columnKey: 'path', title: 'Path' }
                    ]}
                    bodyHeight={460}
                    rows={entityData.details.ous}
                    onRowClick={(item: InventoryEntity) => onEntityClick(item.guid, item.name, AD_ENTITY_TYPES.OU)}
                    EmptyStateProps={{ title: 'No nested ous', icon: EmptyStateIcons.NoItems }}
                />
            );
        default:
            break;
    }
};

const OuDataTable: FC<{
    entityData: EntityData;
    adType: number;
    onEntityClick: (guid?: string, identifier?: string, type?: number) => void;
}> = ({ entityData, adType, onEntityClick }) => {
    const [selectedTab, setSelectedTab] = useState<number>(0);
    const { pricingModulesState } = usePricingModulesState();

    const OU_DATA_TABS = [
        { value: 0, name: 'Properties' },
        { value: 1, name: `Users (${(entityData.details as OUDetails)?.usersCount || 0})` },
        { value: 2, name: `Groups (${(entityData.details as OUDetails)?.groupsCount || 0})` },
        { value: 3, name: `Resources (${(entityData.details as OUDetails)?.resourcesCount || 0})` },
        { value: 4, name: `OUs (${(entityData.details as OUDetails)?.ousCount || 0})` }
    ];

    return (
        <div className="h-full w-full flex flex-col gap-4 px-6 py-4">
            <Tabs selectedTab={selectedTab} onClick={setSelectedTab} tabs={OU_DATA_TABS} />
            {renderTabData(entityData, selectedTab, onEntityClick, pricingModulesState)}
        </div>
    );
};

export default OuDataTable;
